import { Container } from '@/components/ui/container';
import { motion } from 'framer-motion';
import { Clock, Play } from 'lucide-react';
import { explainerVideos } from '@/data/explainer-videos';
import { ExplainerVideo } from '@/types/explainer-video.types';

export default function LandingVideos(): JSX.Element {
	const featured = explainerVideos.find((video) => video.embedUrl);
	const upcoming = explainerVideos.filter((video) => !video.embedUrl);

	const videoStructuredData = featured
		? {
				'@context': 'https://schema.org',
				'@type': 'VideoObject',
				name: featured.title,
				description: featured.description,
				thumbnailUrl: featured.thumbnail,
				embedUrl: featured.embedUrl,
				contentUrl: 'https://youtu.be/SjFuYtmTCoU',
				uploadDate: '2026-07-01',
				publisher: {
					'@type': 'Organization',
					name: 'JovianCloudWorks',
					url: 'https://joviancloudworks.io',
				},
			}
		: null;

	return (
		<Container>
			{videoStructuredData ? (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(videoStructuredData) }}
				/>
			) : null}

			<section id="video" className="scroll-mt-24" aria-labelledby="video-heading">
				<div className="mx-auto mb-10 max-w-3xl text-center">
					<h2
						id="video-heading"
						className="mb-4 font-space-grotesk text-3xl font-bold md:text-4xl"
					>
						What a fractional CTO actually does for a small business
					</h2>
					<p className="text-lg text-muted-foreground">
						Watch how JovianCloudWorks advises and builds — custom systems, workflow automation,
						and dashboards — so tech stops being the thing eating your week.
					</p>
				</div>

				{featured ? (
					<div className="mx-auto mb-12 max-w-4xl">
						<FeaturedVideo video={featured} />
					</div>
				) : null}

				{upcoming.length > 0 ? (
					<div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
						{upcoming.map((video, index) => (
							<UpcomingVideoCard key={video.slug} video={video} index={index} />
						))}
					</div>
				) : null}
			</section>
		</Container>
	);
}

function FeaturedVideo({ video }: { video: ExplainerVideo }): JSX.Element {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className="overflow-hidden"
		>
			<div className="aspect-video overflow-hidden rounded-xl border border-border bg-black shadow-[0_0_40px_rgba(0,0,0,0.25)]">
				<iframe
					src={video.embedUrl}
					title={video.title}
					className="h-full w-full"
					loading="lazy"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
				/>
			</div>
			<div className="mt-5 text-center sm:text-left">
				<p className="text-muted-foreground">{video.description}</p>
			</div>
		</motion.div>
	);
}

function UpcomingVideoCard({
	video,
	index,
}: {
	video: ExplainerVideo;
	index: number;
}): JSX.Element {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
			className="space-card flex flex-col overflow-hidden rounded-xl"
		>
			<div className="relative flex aspect-video items-center justify-center bg-gradient-to-br from-purple-800/40 to-black/40">
				<div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-orange-500/10 px-2.5 py-0.5 text-xs font-medium text-orange-400">
					<Clock className="h-3 w-3" />
					Coming soon
				</div>
				<div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
					<Play className="h-6 w-6 text-white/70" />
				</div>
			</div>
			<div className="p-5">
				<h3 className="mb-2 font-space-grotesk text-lg font-bold">{video.title}</h3>
				<p className="text-sm text-muted-foreground">{video.description}</p>
			</div>
		</motion.div>
	);
}
